import * as d3 from "d3";
import Chart from "@giscon/ui.d3chart";
import { SimpleBoxplotDataset } from "@giscon/ui.d3chart";
import chartConfig from "configs/chartConfig";
import { formatNumber } from "utils/statistic";

const Y_TICK_COUNT = 5;

/** Shared hidden text style for datasets where numeric labels should be invisible */
const HIDDEN_TEXT = {
  min: { anchor: "center", offset: { x: 0, y: 15 }, fill: "transparent" },
  med: { anchor: "center", offset: { x: 0, y: -5 }, fill: "transparent" },
  max: { anchor: "center", offset: { x: 0, y: 0 }, fill: "transparent" },
};

/** Small preview renderer reused for legend items and datasets */
function drawPreview(node, size, drawInfo) {
  const g = d3.select(node);
  // circle in the middle
  g.append("circle")
    .attr("cx", 0)
    .attr("cy", 0)
    .attr("r", Math.max(3, size / 2))
    .attr("fill", drawInfo.colors.fill)
    .attr("stroke", drawInfo.colors.fill);
  // vertical line through the circle
  g.append("line")
    .attr("x1", 0)
    .attr("y1", -size)
    .attr("x2", 0)
    .attr("y2", size)
    .attr("stroke", drawInfo.colors.fill)
    .attr("stroke-width", 1);
}

/**
 * Initialize the dataset legend depending on available datasets.
 * @param {SimpleBoxplotDataset} obsData - Observations dataset.
 * @param {SimpleBoxplotDataset|null} futureData - Future dataset or null.
 */
function initDatasetLegend(obsData, futureData) {
  const hasFuture = !!futureData;

  const items = [
    {
      label: "Beobachtung",
      drawInfo: { colors: { fill: chartConfig.colors.observation } },
      drawPreview,
    },
  ];

  if (hasFuture) {
    items.push(
      {
        label: "RCP2.6",
        drawInfo: { colors: { fill: chartConfig.colors.rcp26 } },
        drawPreview,
      },
      {
        label: "RCP8.5",
        drawInfo: { colors: { fill: chartConfig.colors.rcp85 } },
        drawPreview,
      }
    );
  }

  const dataset = hasFuture ? futureData : obsData;

  dataset.initLegendInfo("", items, true);
}

/** Add a single observation point to `obsData` if the item contains a valid numeric avg. */
function addObservation(obsData, item) {
  const label = item.meta.label;
  const period = chartConfig.periods.obs.find((p) => p.label === label);
  if (!period) return;
  const mid = period.tickMid;
  const avgVal = Number.parseFloat(item.avg);
  if (Number.isNaN(avgVal)) return;
  obsData.add({
    x: mid,
    min: avgVal,
    med: avgVal,
    max: avgVal,
    colors: { fill: chartConfig.colors.observation },
  });
}

/** Add a single future point to `futureData`. */
function addFuturePoint(futureData, item) {
  const { min, max, avg, meta } = item;
  if (!meta || !meta.label) return;
  const period = chartConfig.periods.future.find((p) => p.label === meta.label);
  if (!period) return;
  const mid = period.tickMid;

  const isRcp26 = meta.isRcp26 === true;
  const isRcp85 = meta.isRcp85 === true;
  if (!isRcp26 && !isRcp85) return;

  const shift = 2;

  const minVal = Number.parseFloat(min);
  const medVal = Number.parseFloat(avg);
  const maxVal = Number.parseFloat(max);
  if ([minVal, medVal, maxVal].some((v) => Number.isNaN(v))) return;

  const color = isRcp26 ? chartConfig.colors.rcp26 : chartConfig.colors.rcp85;

  futureData.add({
    x: mid + (isRcp26 ? -shift : shift),
    // stroke color must use the per-point outline because the library ignores fill for strokes
    colors: { fill: color, outline: color },
    min: minVal,
    med: medVal,
    max: maxVal,
  });
}

/**
 * Build and return observation and future datasets from raw table results.
 * @param {Array<object>} tableResult - Array of source rows from API.
 * @returns {{obsData: SimpleBoxplotDataset, futureData: SimpleBoxplotDataset|null}}
 */
function buildDatasets(tableResult) {
  let futureData = null;
  const obsData = new SimpleBoxplotDataset({ style: "point" });

  for (let i = 0; tableResult && i < tableResult.length; i++) {
    const item = tableResult[i];
    if (!item || !item.meta) continue;

    if (item.meta.projection) {
      if (!futureData)
        futureData = new SimpleBoxplotDataset({ style: "point" });
      addFuturePoint(futureData, item);
    } else {
      addObservation(obsData, item);
    }
  }

  return { obsData, futureData };
}

/**
 * Compute x-axis tick values from the obs dataset already populated with data.
 * Filters chartConfig.periods.obs to those whose tickMid is present in obsData,
 * then appends future period ticks when applicable.
 * @param {SimpleBoxplotDataset} obsData
 * @param {boolean} hasFuture
 * @returns {Array<number>} tick values
 */
function computeXTickValues(obsData, hasFuture) {
  const obsXSet = new Set(obsData.data.map((d) => d.x));
  const xTickValues = chartConfig.periods.obs
    .filter((p) => obsXSet.has(p.tickMid))
    .map((p) => p.tickMid);
  if (hasFuture) {
    xTickValues.push(...chartConfig.periods.future.map((p) => p.tickMid));
  }
  return xTickValues;
}

/**
 * Compute the x-axis spec (domain + tick values) from datasets.
 * @param {SimpleBoxplotDataset} obsData
 * @param {SimpleBoxplotDataset|null} futureData
 * @returns {{domain: Array<number>, tickValues: Array<number>}}
 */
function computeDataXAxisSpec(obsData, futureData) {
  const hasFuture = !!futureData;
  const tickValues = computeXTickValues(obsData, hasFuture);
  const obsStart = isFinite(obsData.min.x) ? Math.floor(obsData.min.x) - 5 : 1960;
  const obsEnd = isFinite(obsData.max.x) ? Math.ceil(obsData.max.x) + 5 : 2020;
  return {
    tickValues,
    domain: [obsStart, hasFuture ? 2080 : obsEnd],
  };
}

/**
 * Compute a data-driven y-domain from all available boxplot values.
 * Returns null when no valid numeric values exist.
 * @param {SimpleBoxplotDataset} obsData
 * @param {SimpleBoxplotDataset|null} futureData
 * @returns {Array<number>|null}
 */
function computeDataYDomain(obsData, futureData) {
  const values = [];

  const collectValues = (dataset) => {
    if (!dataset || !Array.isArray(dataset.data)) return;
    for (let i = 0; i < dataset.data.length; i++) {
      const point = dataset.data[i];
      [point.min, point.med, point.max].forEach((v) => {
        if (Number.isFinite(v)) values.push(v);
      });
    }
  };

  collectValues(obsData);
  collectValues(futureData);

  if (!values.length) return null;

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  let lowerBound;
  let upperBound;

  if (minValue === maxValue) {
    const base = Math.abs(minValue) || 1;
    const padding = base * 0.05;
    lowerBound = minValue - padding;
    upperBound = maxValue + padding;
  } else {
    const range = maxValue - minValue;
    const padding = range * 0.05;
    lowerBound = minValue - padding;
    upperBound = maxValue + padding;
  }

  // Keep same-sign domains from crossing zero due to padding.
  if (minValue > 0) {
    lowerBound = Math.max(lowerBound, 0);
  }
  if (maxValue < 0) {
    upperBound = Math.min(upperBound, 0);
  }

  return [lowerBound, upperBound];
}

/**
 * Create a stable y-axis spec (domain + explicit tick values) for data mode.
 * This keeps the axis start/end aligned to labeled ticks.
 * @param {SimpleBoxplotDataset} obsData
 * @param {SimpleBoxplotDataset|null} futureData
 * @param {number} tickCount
 * @returns {{domain: Array<number>|string, tickValues?: Array<number>}}
 */
function computeDataYAxisSpec(obsData, futureData, tickCount) {
  const domain = computeDataYDomain(obsData, futureData);
  if (!domain) {
    return { domain: "auto" };
  }

  const rawMin = domain[0];
  const rawMax = domain[1];
  const step = d3.tickStep(rawMin, rawMax, tickCount);

  if (!Number.isFinite(step) || step <= 0) {
    return { domain };
  }

  const epsilon = step * 1e-6;
  let start = Math.floor(rawMin / step) * step;
  let end = Math.ceil(rawMax / step) * step;

  // Preserve same-sign behavior after tick alignment.
  if (rawMin >= 0) start = Math.max(start, 0);
  if (rawMax <= 0) end = Math.min(end, 0);

  if (end <= start) {
    return { domain };
  }

  const tickValues = [];
  for (let value = start; value <= end + epsilon; value += step) {
    // Normalize floating point artifacts for cleaner labels.
    tickValues.push(Number.parseFloat(value.toFixed(12)));
  }

  if (tickValues.length < 2) {
    return { domain };
  }

  return {
    domain: [tickValues[0], tickValues[tickValues.length - 1]],
    tickValues,
  };
}

/**
 * Build the chart library config object for the x-axis.
 * @param {{tickValues: Array<number>, domain: Array<number>}} xAxisSpec
 * @returns {object}
 */
function buildXAxisConfig({ tickValues, domain }) {
  return {
    positioning: "bottom",
    id: "x",
    tickValues,
    tickFormat: (x) => {
      const allPeriods = [
        ...chartConfig.periods.obs,
        ...chartConfig.periods.future,
      ];
      const period = allPeriods.find((p) => p.tickMid === x);
      return period ? period.label : "";
    },
    domain,
    // unit: "Jahre",
  };
}

/**
 * Build the chart library config object for the y-axis.
 * @param {{domain: Array<number>|string, tickValues?: Array<number>}} yAxisSpec
 * @param {number} yAxisDecimalPlaces
 * @returns {object}
 */
function buildYAxisConfig(yAxisSpec, yAxisDecimalPlaces) {
  const config = {
    positioning: "left",
    id: "y",
    ticks: Y_TICK_COUNT,
    domain: yAxisSpec.domain,
    type: "y",
    // unit: "Jahre",
    tickFormat: (v) => formatNumber(v, yAxisDecimalPlaces),
  };
  if (Array.isArray(yAxisSpec.tickValues) && yAxisSpec.tickValues.length > 0) {
    config.tickValues = yAxisSpec.tickValues;
  }
  return config;
}

/**
 * Add x and y axes to a chart instance.
 * @param {Chart} chart
 * @param {{tickValues: Array<number>, domain: Array<number>}} xAxisSpec
 * @param {{domain: Array<number>|string, tickValues?: Array<number>}} yAxisSpec
 * @param {number} yAxisDecimalPlaces
 */
function addAxes(chart, xAxisSpec, yAxisSpec, yAxisDecimalPlaces) {
  chart.addAxis(buildXAxisConfig(xAxisSpec));
  chart.addAxis(buildYAxisConfig(yAxisSpec, yAxisDecimalPlaces));
}

/**
 * Attach datasets to the chart and initialize the chart legend.
 * @param {Chart} chart
 * @param {SimpleBoxplotDataset} obsData
 * @param {SimpleBoxplotDataset|null} futureData
 */
function addDatasetsToChart(chart, obsData, futureData) {
  if (futureData) chart.addDataset(futureData);
  chart.addDataset(obsData);
}

/**
 * Initialize chart-level legend and dataset legend items.
 * @param {Chart} chart
 * @param {SimpleBoxplotDataset} obsData
 * @param {SimpleBoxplotDataset|null} futureData
 */
function setupLegend(chart, obsData, futureData) {
  // initialize the chart-level legend (positions and size)
  chart.initLegend({
    orientation: { x: "center", y: "top", yOffset: 15, xOffset: 5 },
    size: chartConfig.legend.size,
    width: chartConfig.legend.width,
  });

  // initialize dataset-level legend content (labels + previews)
  initDatasetLegend(obsData, futureData);
}

/**
 * Draw a horizontal baseline at y=0 as an SVG overlay for the chart.
 * @param {Chart} chart
 */
function drawBaseline(chart) {
  const svg = chart.svg;
  const axisMap = chart.axisMap;
  if (!svg || !axisMap || !axisMap.x || !axisMap.y) return;
  // remove any existing baseline overlay for this chart
  svg.selectAll(".baseline-overlay").remove();
  const group = svg.append("g").attr("class", "baseline-overlay");
  const yPixel = axisMap.y.scale(0);
  const xRange =
    typeof axisMap.x.scale.range === "function"
      ? axisMap.x.scale.range()
      : [0, chart.width || 0];
  group
    .append("line")
    .attr("x1", xRange[0])
    .attr("y1", yPixel)
    .attr("x2", xRange[1])
    .attr("y2", yPixel)
    .attr("stroke", "#000")
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "6,4");
}

/**
 * Wrap the chart's draw method to include a zero baseline overlay when requested.
 * @param {Chart} chart
 * @param {boolean} showZeroBaseline
 */
function attachZeroBaseline(chart, showZeroBaseline) {
  if (!showZeroBaseline) return;
  // If `showZeroBaseline` is requested we draw a scoped overlay line at y=0.
  const originalDraw = chart.draw.bind(chart);
  chart.draw = (create = true) => {
    originalDraw(create);
    try {
      drawBaseline(chart);
    } catch (e) {
      /* eslint-disable no-console */
      console.warn("chartUtil: could not attach baseline overlay", e);
    }
  };
}

/**
 * Create options object for constructing a Chart instance.
 * @param {object} params
 * @param {Array<number>} params.gridLines
 * @returns {object} chart options
 */
function makeChartOptions({ gridLines }) {
  return {
    height: 500,
    width: 500,
    padding: { left: 47, bottom: 105, right: 5, top: 45 },
    customGridLines: { x: gridLines },
    css: `
      .tick, .chart-label { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif; font-size: 16px; }
      .legend-wrapper { overflow: visible !important; }
      .legend-wrapper .single-legend { transform: translate(0px, 0px) !important; }
      .legend-wrapper .single-legend-item { transform: translate(0px, 0px) !important; }
      .legend-wrapper .single-legend-item:nth-child(1) { transform: translate(0px, 0px) !important; }
      .legend-wrapper .single-legend-item:nth-child(2) { transform: translate(140px, 0px) !important; }
      .legend-wrapper .single-legend-item:nth-child(3) { transform: translate(280px, 0px) !important; }
      .single-legend-item-label { dominant-baseline: middle !important; }
    `,
  };
}

/**
 * Initialize axes, datasets and baseline for a Chart instance.
 * @param {Chart} chart
 * @param {object} params
 * @param {SimpleBoxplotDataset} params.obsData
 * @param {SimpleBoxplotDataset|null} params.futureData
 * @param {{tickValues: Array<number>, domain: Array<number>}} params.xAxisSpec
 * @param {{domain: Array<number>|string, tickValues?: Array<number>}} params.yAxisSpec
 * @param {boolean} params.showZeroBaseline
 * @param {number} params.yAxisDecimalPlaces
 */
function initializeChart(
  chart,
  {
    obsData,
    futureData,
    xAxisSpec,
    yAxisSpec,
    showZeroBaseline,
    yAxisDecimalPlaces,
  }
) {
  addAxes(chart, xAxisSpec, yAxisSpec, yAxisDecimalPlaces);
  addDatasetsToChart(chart, obsData, futureData);
  setupLegend(chart, obsData, futureData);
  attachZeroBaseline(chart, showZeroBaseline);
}

/**
 * Prepare datasets by building them and applying default drawInfo configuration.
 * @param {Array<object>} tableResult
 * @returns {{obsData: SimpleBoxplotDataset, futureData: SimpleBoxplotDataset|null}}
 */
function prepareDatasets(tableResult) {
  // Build datasets: historical observations as points (black) and futures as box/point markers
  const { obsData, futureData } = buildDatasets(tableResult);

  // configure hidden text
  obsData.drawInfo.text = HIDDEN_TEXT;
  if (futureData) {
    futureData.drawInfo.text = HIDDEN_TEXT;
    // SimpleBoxplotDataset defaults medianOutline to black; clear it so per-point outlines take over
    futureData.drawInfo.medianOutline = null;
  }
  return { obsData, futureData };
}

/**
 * Prepare chart options, x-axis spec and y-axis spec from datasets and config parameters.
 * @param {object} params
 * @param {SimpleBoxplotDataset} params.obsData
 * @param {SimpleBoxplotDataset|null} params.futureData
 * @param {"auto"|"data"} params.yDomainMode
 * @returns {{options: object, xAxisSpec: object, yAxisSpec: object}}
 */
function prepareChartConfig({ obsData, futureData, yDomainMode }) {
  const xAxisSpec = computeDataXAxisSpec(obsData, futureData);
  // Align gridlines with the axis tick positions so gridlines fall exactly under the tick labels used on the x-axis.
  const gridLines = [...xAxisSpec.tickValues];
  const options = makeChartOptions({ gridLines });

  const yAxisSpec =
    yDomainMode === "data"
      ? computeDataYAxisSpec(obsData, futureData, Y_TICK_COUNT)
      : { domain: "auto" };

  return { options, xAxisSpec, yAxisSpec };
}

/**
 * Create and return a configured Chart instance for the given table result.
 * @param {object} params
 * @param {Array<object>} params.tableResult - API table rows to visualize.
 * @param {string} [params.units] - Units (reserved for future use).
 * @param {boolean} [params.showZeroBaseline=false] - Whether to show baseline at y=0.
 * @param {number|string} [params.yAxisDecimalPlaces=1] - Preferred decimals for y-axis labels.
 * @param {"auto"|"data"} [params.yDomainMode="auto"] - Mode for y-axis domain calculation.
 * @returns {Chart} Configured chart instance.
 */
export function createChart({
  tableResult,
  units,
  showZeroBaseline = false,
  yAxisDecimalPlaces = 1,
  yDomainMode = "auto",
}) {
  const parsedYAxisDecimalPlaces = Number(yAxisDecimalPlaces);
  const normalizedYAxisDecimalPlaces =
    Number.isFinite(parsedYAxisDecimalPlaces) && parsedYAxisDecimalPlaces >= 0
      ? Math.floor(parsedYAxisDecimalPlaces)
      : 1;

  const { obsData, futureData } = prepareDatasets(tableResult);

  const { options, xAxisSpec, yAxisSpec } = prepareChartConfig({
    obsData,
    futureData,
    yDomainMode,
  });

  const newChart = new Chart(options);

  initializeChart(newChart, {
    obsData,
    futureData,
    xAxisSpec,
    yAxisSpec,
    showZeroBaseline,
    yAxisDecimalPlaces: normalizedYAxisDecimalPlaces,
  });

  // initial draw so baseline is visible immediately if requested
  newChart.draw();
  return newChart;
}

export default createChart;
